import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["alert", "breadcrumbs", "badge", "children", "details", "flush", "fullWidth", "headingLevel", "role", "tabs", "title", "titleControls", "use"];
/* eslint-disable @typescript-eslint/no-shadow */
import { useEffect, useRef, useState, createContext, useContext } from 'react';
import styled, { css } from 'foundations-theming/utils';
import { TEXT_RESET_STYLES } from '../../../components/Text';
import { filterProps } from '../../../utils/filterProps';
import { isIgnoredNode } from '../../../utils/isIgnoredNode';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { ButtonWrapper } from '../ButtonWrapper';
import { FlexContainer } from '../ButtonWrapper/ButtonWrapper';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CONSTRAINED_TITLE_WIDTH = 544;
const pseudoBorderMixin = css(["&::after{content:' ';position:absolute;left:", ";bottom:0;width:calc( 100% - ", " * 2 );height:", ";background-color:", ";}"], ({
  theme
}) => theme.space[1100], ({
  theme
}) => theme.space[1100], ({
  theme
}) => theme.space[25], ({
  theme
}) => theme.color['border-core-default']);
export const Outer = styled.header.withConfig({
  displayName: "Header__Outer",
  componentId: "sc-1n2sxz1-0"
})(({
  theme,
  $condensed,
  $fullWidth,
  $tabs,
  $flush,
  $cssStyles
}) => {
  return Object.assign({
    display: 'flex',
    position: 'relative',
    flexDirection: 'column',
    marginBottom: $condensed ? '0' : theme.space[500],
    paddingBottom: $tabs ? '0' : theme.space[500],
    paddingLeft: theme.space[1100],
    paddingRight: theme.space[1100],
    paddingTop: theme.space[500]
  }, $condensed || $tabs ? {} : Object.assign({
    borderBottom: $fullWidth ? `${theme.space[25]} solid ${theme.color['border-transitional-core-subtle']}` : undefined
  }, !$fullWidth ? pseudoBorderMixin : {}), $flush ? {
    paddingLeft: theme.space[0],
    paddingRight: theme.space[0],
    '&::after': {
      left: 0,
      width: '100%'
    }
  } : {}, $cssStyles);
});
const Inner = styled.div.withConfig({
  displayName: "Header__Inner",
  componentId: "sc-1n2sxz1-1"
})(({
  theme,
  $tabs,
  $details
}) => {
  let paddingBottom = theme.space[0];

  // In UIHeader, the 20px here was set as padding top in UIHeaderDetails.
  // The code below is a workaround that assumes when $details is an object
  // and not null, the user passed the HeaderDetails component. But when the
  // user passes a string, we don't want to add the padding.
  // The more robust solution would be to create a Context that allows us
  // to set the top padding in the HeaderDetails component to replicate
  // the behavior of UIHeaderDetails more accurately.
  if (typeof $details === 'object' && $details !== null && !$tabs) {
    paddingBottom = theme.space[500];
  } else if ($tabs) {
    paddingBottom = theme.space[200];
  }
  return {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    minHeight: theme.space[800],
    paddingBottom
  };
});
const Alert = styled.div.withConfig({
  displayName: "Header__Alert",
  componentId: "sc-1n2sxz1-2"
})(({
  theme,
  $tabs
}) => {
  return {
    marginTop: theme.space[500],
    marginBottom: $tabs && theme.space[500]
  };
});
const ToolBarWrapper = styled.div.withConfig({
  displayName: "Header__ToolBarWrapper",
  componentId: "sc-1n2sxz1-3"
})(["flex-grow:", ";margin-left:auto;& ", "{column-gap:0;}"], ({
  $widthConstrained
}) => $widthConstrained ? 1 : 0, FlexContainer);
const Toolbar = styled(ButtonWrapper).attrs({
  justify: 'end'
}).withConfig({
  displayName: "Header__Toolbar",
  componentId: "sc-1n2sxz1-4"
})([""]);
const Title = styled.div.withConfig({
  displayName: "Header__Title",
  componentId: "sc-1n2sxz1-5"
})(["max-width:100%;", ""], ({
  $widthConstrained
}) => !$widthConstrained && css(["display:flex;flex-basis:0;flex-grow:1;align-items:center;"]));
const Heading = styled.h1.withConfig({
  displayName: "Header__Heading",
  componentId: "sc-1n2sxz1-6"
})(({
  theme,
  as,
  $solo
}) => {
  const typographyStyles = {
    h1: theme.typography['heading-300'],
    h2: theme.typography['heading-500'],
    h3: theme.typography['heading-400'],
    h4: theme.typography['heading-300'],
    h5: theme.typography['heading-100'],
    h6: theme.typography['heading-50']
  };
  return Object.assign({}, TEXT_RESET_STYLES, typographyStyles[as], {
    marginBottom: theme.space[0],
    maxWidth: '100%',
    paddingRight: $solo ? theme.space[0] : theme.space[700],
    '@media screen and (-webkit-min-device-pixel-ratio: 0)': {
      marginBottom: theme.space[50],
      '@supports (-ms-ime-align: auto)': {
        marginBottom: theme.space[0]
      }
    }
  });
});
const TitleBadge = styled.div.withConfig({
  displayName: "Header__TitleBadge",
  componentId: "sc-1n2sxz1-7"
})(["flex-shrink:0;margin-right:", ";white-space:nowrap;"], ({
  theme
}) => theme.space[300]);
const Badge = styled.span.withConfig({
  displayName: "Header__Badge",
  componentId: "sc-1n2sxz1-8"
})(["margin-left:", ";vertical-align:middle;"], ({
  theme
}) => theme.space[200]);
const HeaderContext = /*#__PURE__*/createContext({
  header: false
});
export const useHeaderContext = () => {
  const headerContext = useContext(HeaderContext);
  return headerContext;
};
export const Header = _ref => {
  let {
      alert,
      breadcrumbs,
      badge,
      children,
      details,
      flush = false,
      fullWidth = true,
      headingLevel = 'h1',
      role = 'banner',
      tabs,
      title,
      titleControls,
      use
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const [widthConstrained, setWidthConstrained] = useState(false);
  const headerRef = useRef(null);
  const titleRef = useRef(null);
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  useEffect(() => {
    const measureWidth = () => {
      const headerEl = headerRef.current;
      if (!headerEl) return;
      const newWidthConstrained = headerEl.offsetWidth < CONSTRAINED_TITLE_WIDTH;
      setWidthConstrained(newWidthConstrained);
    };
    measureWidth();
    window.addEventListener('resize', measureWidth);
    return () => {
      window.removeEventListener('resize', measureWidth);
    };
  }, []);
  const renderTitle = (title, badge) => {
    if (!badge) return title;
    return /*#__PURE__*/_jsxs("span", {
      children: [title, /*#__PURE__*/_jsx(Badge, {
        children: badge
      })]
    });
  };
  const renderHeading = (headingLevel, badge, children, title, titleControls) => {
    return /*#__PURE__*/_jsxs(Title, {
      ref: titleRef,
      $widthConstrained: widthConstrained,
      children: [/*#__PURE__*/_jsx(Heading, {
        as: headingLevel,
        $solo: !children && !titleControls,
        children: renderTitle(title, badge)
      }), titleControls && /*#__PURE__*/_jsx(TitleBadge, {
        children: titleControls
      })]
    });
  };
  return /*#__PURE__*/_jsx(HeaderContext.Provider, {
    value: {
      header: true
    },
    children: /*#__PURE__*/_jsxs(Outer, Object.assign({
      ref: headerRef,
      role: "presentation",
      $condensed: use === 'condensed',
      $fullWidth: fullWidth,
      $tabs: !isIgnoredNode(tabs),
      $flush: flush,
      $cssStyles: cssStyles
    }, domProps, {
      children: [breadcrumbs, /*#__PURE__*/_jsxs(Inner, {
        $tabs: !isIgnoredNode(tabs),
        $details: details,
        role: role,
        children: [title && renderHeading(headingLevel, badge, children, title, titleControls), children && /*#__PURE__*/_jsx(ToolBarWrapper, {
          $widthConstrained: widthConstrained,
          children: /*#__PURE__*/_jsx(Toolbar, {
            children: children
          })
        })]
      }), details, alert && /*#__PURE__*/_jsx(Alert, {
        $tabs: !isIgnoredNode(tabs),
        children: alert
      }), tabs]
    }))
  });
};